import ChatService from "@/api/chat.service.js";

const STORAGE_MANUAL_OFFLINE = "chat_operator_manual_offline";
const STORAGE_AUTO_ONLINE_ACTIVE = "chat_operator_auto_online_active";
const TOUCH_INTERVAL_MS = 30000;

const ChatOperatorPresenceService = {
  heartbeatInterval: null,
  activityThrottle: null,
  routerUnregister: null,
  started: false,
  mode: "auto",
  lastTouchAt: 0,

  start(router) {
    if (this.started) {
      return;
    }

    this.started = true;

    this.startAutoOnline();
    this.bindActivityListeners();
    this.bindVisibilityListener();

    if (router && typeof router.afterEach === "function") {
      this.routerUnregister = router.afterEach(() => {
        this.touchThrottled();
      });
    }
  },

  stop() {
    this.started = false;
    this.stopHeartbeat();
    this.removeActivityListeners();
    this.removeVisibilityListener();

    if (typeof this.routerUnregister === "function") {
      this.routerUnregister();
      this.routerUnregister = null;
    }
  },

  async startAutoOnline() {
    if (localStorage.getItem(STORAGE_MANUAL_OFFLINE) === "1") {
      return;
    }

    try {
      const response = await ChatService.autoOnline();

      if (!response || !response.data || response.data.online !== true) {
        localStorage.removeItem(STORAGE_AUTO_ONLINE_ACTIVE);
        return;
      }

      this.mode = "auto";
      localStorage.setItem(STORAGE_AUTO_ONLINE_ACTIVE, "1");

      await this.touchNow(this.mode);

      this.lastTouchAt = Date.now();

      this.startHeartbeat("auto");
      this.notifyStatusChanged(true);
    } catch (error) {
      console.error("Chat operator auto online failed", error);
    }
  },

    startHeartbeat(mode = "auto") {
        this.stopHeartbeat();

        this.mode = mode;

        this.heartbeatInterval = window.setInterval(() => {
            this.touchThrottled();
        }, TOUCH_INTERVAL_MS);
    },

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      window.clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  },

    touchThrottled(force = false) {
      if (localStorage.getItem(STORAGE_MANUAL_OFFLINE) === "1") {
        return;
      }

      if (!this.isErpTabActive()) {
        return;
      }

      const now = Date.now();

      if (!force && this.lastTouchAt && now - this.lastTouchAt < TOUCH_INTERVAL_MS) {
        return;
      }

      this.lastTouchAt = now;
      this.touchNow(this.mode);
    },

  async touchNow(mode = this.mode) {
      if (localStorage.getItem(STORAGE_MANUAL_OFFLINE) === "1") {
        return;
      }

      if (!this.isErpTabActive()) {
        return;
      }

      try {
        const response = await ChatService.touchOperator({
          mode,
        });

        if (mode === "auto" && response.data && response.data.online !== true) {
          this.stopHeartbeat();
          localStorage.removeItem(STORAGE_AUTO_ONLINE_ACTIVE);
          this.notifyStatusChanged(false);
          return;
        }

        if (response.data && response.data.online === true) {
          this.notifyStatusChanged(true);
        }
      } catch (error) {
        console.error("Chat operator touch failed", error);
      }
    },

  bindActivityListeners() {
    ["click", "keydown", "scroll", "mousemove", "touchstart"].forEach((eventName) => {
      window.addEventListener(eventName, this.handleActivity, { passive: true });
    });
  },

  removeActivityListeners() {
    ["click", "keydown", "scroll", "mousemove", "touchstart"].forEach((eventName) => {
      window.removeEventListener(eventName, this.handleActivity);
    });

    if (this.activityThrottle) {
      window.clearTimeout(this.activityThrottle);
      this.activityThrottle = null;
    }
  },

    handleActivity() {
        ChatOperatorPresenceService.touchThrottled();
    },

    bindVisibilityListener() {
      document.addEventListener("visibilitychange", this.handleVisibilityChange);
      window.addEventListener("focus", this.handleWindowFocus);
    },

    removeVisibilityListener() {
      document.removeEventListener("visibilitychange", this.handleVisibilityChange);
      window.removeEventListener("focus", this.handleWindowFocus);
    },

    handleWindowFocus() {
      ChatOperatorPresenceService.touchThrottled(true);
    },

    isErpTabActive() {
        return document.visibilityState === "visible" && !document.hidden && document.hasFocus();
    },

    handleVisibilityChange() {
    if (document.hidden || document.visibilityState !== "visible") {
        return;
    }

    ChatOperatorPresenceService.touchThrottled(true);
    },

  async setManualOnline() {
    try {
      await ChatService.setOperatorOnline();

      localStorage.removeItem(STORAGE_MANUAL_OFFLINE);
      localStorage.removeItem(STORAGE_AUTO_ONLINE_ACTIVE);

      this.mode = "manual";
      this.startHeartbeat("manual");
      this.notifyStatusChanged(true);
    } catch (error) {
      console.error("Set operator online failed", error);
    }
  },

  async setManualOffline() {
    try {
      await ChatService.setOperatorOffline();

      localStorage.setItem(STORAGE_MANUAL_OFFLINE, "1");
      localStorage.removeItem(STORAGE_AUTO_ONLINE_ACTIVE);

      this.stopHeartbeat();
      this.notifyStatusChanged(false);
    } catch (error) {
      console.error("Set operator offline failed", error);
    }
  },

  notifyStatusChanged(isOnline) {
    window.dispatchEvent(new CustomEvent("chat-operator-status-changed", {
      detail: {
        is_online: isOnline,
      },
    }));
  },
};

export default ChatOperatorPresenceService;